/** @jsxImportSource react */
/** @jsxFactory React.createElement */
/** @jsxFragmentFactory React.Fragment */

import { faEraser, faFileImage, faTrash, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ForwardedRef, forwardRef, useEffect, useMemo, useState } from 'react';

export interface DrawingProps {
	width: number;
	height: number;
	ensureControlsInViewport?: boolean;
	config?: Partial<DrawingConfig>;
	extraCSS?: string;
	onDrawDone?: (imageData: string) => void;
}

export interface DrawingConfig {
	brushSizes: number[];
	colors: string[];
	colorNames: string[];
	backgroundImage?: string;
}

const defaultConfig: DrawingConfig = {
	brushSizes: [5, 10, 15], // [5, 10, 20, 35, 50],
	colors: ['#FF4500', '#000000', '#A0522D', '#808080', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#00FFFF', '#FF00FF', '#FFFFFF'],
	colorNames: ['Red-Orange', 'Black', 'Brown', 'Gray', 'Red', 'Green', 'Blue', 'Yellow', 'Cyan', 'Magenta', 'White']
};

const Drawing = forwardRef<HTMLDivElement, DrawingProps>((props: DrawingProps, fRef: ForwardedRef<HTMLDivElement>) => {
	const { width, height, extraCSS, ensureControlsInViewport, config: configProp, onDrawDone } = props;
	const config = { ...defaultConfig, ...configProp };

	const [canvas, setCanvas] = useState<HTMLCanvasElement | null>();
	const [brushSize, setBrushSize] = useState(config.brushSizes[Math.floor(config.brushSizes.length / 2)]);
	const [color, setColor] = useState(config.colors[0]);
	const context = useMemo(() => canvas?.getContext('2d'), [canvas]);
	const [lastImageData, setLastImageData] = useState<ImageData | null>(null);

	useEffect(() => {
		if (!canvas) return;
		// if this isn't set to absolute values, very weird things happen
		const bounds = canvas.getBoundingClientRect();
		canvas.width = bounds.width;
		canvas.height = bounds.height;
	}, [canvas]);

	useEffect(() => {
		if (!canvas || !context) return;
		// add eventlistener so we set the size again when the canvas size changes
		const onResize = () => {
			const bounds = canvas.getBoundingClientRect();
			canvas.width = bounds.width;
			canvas.height = bounds.height;
		};

		let isDrawing = false;
		let lastX = 0;
		let lastY = 0;
		let drawDoneTimeout: NodeJS.Timeout;

		const draw = (newX: number, newY: number): void => {
			// console.log('DRAW', [lastX, lastY], [newX, newY], e);
			context.strokeStyle = color === 'transparent' ? 'black' : color;
			context.lineWidth = brushSize;
			context.lineCap = 'round';
			context.globalCompositeOperation = color === 'transparent' ? 'destination-out' : 'source-over';
			context.beginPath();
			context.moveTo(lastX, lastY);
			context.lineTo(newX, newY);
			context.stroke();
			lastX = newX;
			lastY = newY;
			clearTimeout(drawDoneTimeout);
			drawDoneTimeout = setTimeout(() => {
				onDrawDone?.(createResizedImage(canvas, width, height));
			}, 100);
		};

		const onMouseDown = (e: MouseEvent): void => {
			setLastImageData(context.getImageData(0, 0, canvas.width, canvas.height));
			// console.log('onMouseDown');
			isDrawing = true;
			const newX = e.offsetX;
			const newY = e.offsetY;
			lastX = newX;
			lastY = newY;
		};
		const onTouchStart = (e: TouchEvent): void => {
			e.preventDefault();
			isDrawing = true;
			const bounds = canvas.getBoundingClientRect();
			// console.log('onTouchStart', e.touches[0].clientX - bounds.left, e.touches[0].clientY - bounds.top);
			[lastX, lastY] = [e.touches[0].clientX - bounds.left, e.touches[0].clientY - bounds.top];
		};
		const onMouseMove = (e: MouseEvent): void => {
			// console.log('onMouseMove');
			if (!isDrawing) return;
			const newX = e.offsetX;
			const newY = e.offsetY;
			draw(newX, newY);
		};
		const onTouchMove = (e: TouchEvent): void => {
			e.preventDefault();
			if (!isDrawing) return;
			const bounds = canvas.getBoundingClientRect();
			// console.log('onTouchMove', e.touches[0].clientX - bounds.left, e.touches[0].clientY - bounds.top);
			draw(e.touches[0].clientX - bounds.left, e.touches[0].clientY - bounds.top);
		};
		const drawStop = () => {
			// console.log('onMouseUpOrOut');
			isDrawing = false;
		};

		window.addEventListener('resize', onResize);
		canvas.addEventListener('mousedown', onMouseDown);
		canvas.addEventListener('touchstart', onTouchStart);
		canvas.addEventListener('touchmove', onTouchMove);
		canvas.addEventListener('mousemove', onMouseMove);
		canvas.addEventListener('mouseup', drawStop);
		canvas.addEventListener('mouseout', drawStop);
		canvas.addEventListener('touchend', drawStop);
		canvas.addEventListener('touchcancel', drawStop);
		return () => {
			window.removeEventListener('resize', onResize);
			canvas.removeEventListener('mousedown', onMouseDown);
			canvas.removeEventListener('touchstart', onTouchStart);
			canvas.removeEventListener('touchmove', onTouchMove);
			canvas.removeEventListener('mousemove', onMouseMove);
			canvas.removeEventListener('mouseup', drawStop);
			canvas.removeEventListener('mouseout', drawStop);
			canvas.removeEventListener('touchend', drawStop);
			canvas.removeEventListener('touchcancel', drawStop);
		};
	}, [canvas, context, color, brushSize, height, width, onDrawDone]);

	// this ensures that the canvas doesn't get re-rendered on every change and thus losing its state
	const Canvas = useMemo(() => {
		return (
			<canvas
				id="myCanvas"
				ref={setCanvas}
				className="touch-none bg-contain grow bg-no-repeat bg-gray-700 cursor-tangia-pen"
				style={{
					aspectRatio: `${width}/${height}`,
					maxHeight: ensureControlsInViewport ? 'calc(100% - 60px)' : undefined,
					width: ensureControlsInViewport ? undefined : '100%',
					backgroundImage: `url('${config.backgroundImage}')`
				}}
			/>
		);
	}, [width, height, config.backgroundImage, ensureControlsInViewport]);

	return (
		<div id="drawing" ref={fRef}
				 className={`flex flex-col items-center relative bg-black text-white ${ensureControlsInViewport ? 'h-full' : ''}`}>
			<style>{extraCSS}</style>
			{Canvas}
			<div id="controls" className="mt-2 px-1 flex flex-wrap-reverse place-content-around items-center">
				<button
					id="undo-btn"
					type="button"
					title="Undo last change"
					className="px-3 ml-2 self-center rounded-md aspect-square text-lg text-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed hover:bg-gray-700"
					disabled={!lastImageData}
					aria-disabled={!lastImageData}
					onClick={() => {
						context!.putImageData(lastImageData!, 0, 0);
						setLastImageData(null);
						onDrawDone?.(createResizedImage(canvas!, width, height));
					}}
				>
					<FontAwesomeIcon icon={faUndo} className="w-4 h-4 pointer-events-none" />
				</button>
				<button
					id="img-import-btn"
					title="Import image"
					type="button"
					className="px-3 self-center rounded-md aspect-square text-lg text-gray-200 hover:bg-gray-700"
					onClick={() => {
						// open file dialog
						const fileInput = document.createElement('input');
						fileInput.type = 'file';
						fileInput.accept = 'image/*';
						fileInput.addEventListener('change', () => {
							const file = fileInput.files![0];
							const reader = new FileReader();
							// load the image
							reader.addEventListener('load', () => {
								const img = new Image();
								img.src = reader.result as string;
								// place the image on the canvas
								img.addEventListener('load', () => {
									setLastImageData(context!.getImageData(0, 0, canvas!.width, canvas!.height));
									const canvasAspectRatio = canvas!.width / canvas!.height;
									const imgAspectRatio = img.width / img.height;
									if (imgAspectRatio > canvasAspectRatio) {
										// center vertically
										const newHeight = canvas!.width * img.height / img.width;
										context!.drawImage(img, 0, Math.floor((canvas!.height - newHeight) / 2), canvas!.width, newHeight);
									} else {
										// center horizontally
										const newWidth = canvas!.height * img.width / img.height;
										context!.drawImage(img, Math.floor((canvas!.width - newWidth) / 2), 0, newWidth, canvas!.height);
									}
									onDrawDone?.(createResizedImage(canvas!, width, height));
								});
							});
							reader.readAsDataURL(file);
						});

						fileInput.click();
					}}
				>
					<FontAwesomeIcon icon={faFileImage} className="w-4 h-4 pointer-events-none" />
				</button>
				<button
					id="reset-btn"
					type="button"
					title="Clear drawing"
					className="px-2 self-center rounded-md aspect-square text-lg text-gray-200 hover:bg-gray-700"
					onClick={() => {
						setLastImageData(context!.getImageData(0, 0, canvas!.width, canvas!.height));
						context!.clearRect(0, 0, canvas!.width, canvas!.height);
					}}
				>
					<FontAwesomeIcon icon={faTrash} className="w-4 h-4 pointer-events-none" />
				</button>

				<div className="flex flex-col ml-2">
					<div id="brushSize" className="shrink-0 flex text-xs">
						{config.brushSizes.map((size, idx, all) => (
							<button
								key={size}
								type="button"
								className={
									'flex px-1 items-center place-content-around shrink-0 whitespace-nowrap ' +
									(brushSize === size ? 'bg-white text-black hover:bg-orange-300' : 'bg-black text-white border border-white hover:border-orange-300') +
									' ' +
									(idx === 0 ? 'rounded-l-lg' : idx === all.length - 1 ? 'rounded-r-lg' : null)
								}
								onClick={() => setBrushSize(size)}
							>
								<div
									className="rounded-full mr-1 my-1 bg-current shrink-0"
									style={{
										width: `${size}px`,
										height: `${size}px`
									}}
								></div>
								{' '}
								{idxToSize(idx)}
							</button>
						))}
					</div>
				</div>

				<div className="flex ml-4 flex-col grow">
					<div id="color" className="mr-2 flex flex-wrap shrink-0 items-center">
						{config.colors.map(c => (
							<button
								key={c}
								type="button"
								className={'rounded-full my-1 mr-2 h-[15px] w-[15px] hover:opacity-75 outline ' + (color === c ? 'outline-gray-300' : 'outline-gray-500')}
								onClick={() => setColor(c)}
								style={{
									backgroundColor: c,
									outlineWidth: color === c ? '4px' : '2px'
								}}
							></button>
						))}
						<button
							key="ereaser"
							type="button"
							className={'rounded-full mr-2 text-gray-200 hover:text-white ' + (color === 'transparent' ? 'outline outline-2 outline-gray-300' : null)}
							onClick={() => setColor('transparent')}
						>
							<FontAwesomeIcon icon={faEraser} className="w-4 h-4 pointer-events-none" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
});
Drawing.displayName = 'Drawing';

const createResizedImage = (canvas: HTMLCanvasElement, new_width: number, new_height: number) => {
	// Create a new canvas element with the same aspect ratio as the original canvas
	const new_canvas = document.createElement('canvas');
	new_canvas.width = new_width;
	new_canvas.height = new_height;

	// Draw the original canvas onto the new canvas
	const ctx = new_canvas.getContext('2d');
	ctx!.drawImage(canvas, 0, 0, new_width, new_height);

	// Convert the new canvas to a base64-encoded PNG image
	const dataURL = new_canvas.toDataURL('image/png');
	return dataURL;
};

export { Drawing };

function idxToSize(idx: number) {
	switch (idx) {
		case 0:
			return 'SM';
		case 1:
			return 'MD';
		case 2:
			return 'LG';
		case 3:
			return 'XL';
		default:
			return idx - 2 + 'XL';
	}
}
